import React, { useState, useRef, useEffect } from 'react'
import Calendar from 'react-calendar'
import moment from 'moment'
import { useTranslation } from 'react-i18next'

import Modal from '../Modal'
import {
  ModalWrapper,
  ModalContainer,
  CloseButtonContainer,
  ModalContent,
  ModalHeader,
  ModalTitle,
  ModalFooter
} from '../containers/Webview/WebviewModal/WebviewModal.style'
import {
  CloseWidgetIcon,
  DatepickerPreviousIcon,
  DatepickerNextIcon
} from '../../icons/Icons'
import './Calendar.scss'
import MessageButton from '../message/MessageButton'
import { calendarRange } from '../../constants'
import { savePointNameToStorage, removeStorageKeyFromBrowser } from '../../actions/static'
import { palette } from '../../theme/palette'
import { focusTrapCreator } from '../../helper/focusTrapCreator'
import { DatePickerSettingsType } from '../../models/DatePickerSettingsType'

import { formatDisplayBirthDate, formatDisplayAppointmentDate, formatMonth, formatMonthYear, formatShortWeekday } from './dateFormatter'

interface Props {
  onChange: (formattedDate: string, displayDate: string) => void
  datePickerSettings: DatePickerSettingsType
  setDatePickerSettings: (datePickerSettings: DatePickerSettingsType) => void
  isLoadingIframeData: boolean
  language: string
}

const SelectDateModal: React.FC<Props> = ({ onChange, datePickerSettings, setDatePickerSettings, isLoadingIframeData, language }) => {
  const { t } = useTranslation()
  const utcDate = datePickerSettings.currenDateValue || new Date(moment.utc().format())
  const [value, setValue] = useState(utcDate)
  const [buttonTitle, setButtonTitle] = useState(t('select'))
  const formattedDate = moment(value).format('YYYY-MM-DD')
  const defaultMinDate = moment().subtract(calendarRange.MIN_DATE, 'years').toDate()
  const defaultMaxDate = moment().add(calendarRange.MAX_DATE, 'years').toDate()
  const [isButtonDisabled, setIsButtonDisabled] = useState(true)
  const datePickerModalRef = useRef(null)
  const closeDatePickerButtonRef = useRef(null)

  useEffect(() => {
    closeDatePickerButtonRef.current.focus()
    if (!datePickerSettings.selectedPickerDate) {
      setDatePickerSettings({
        ...datePickerSettings,
        selectedPickerDate: false
      })
    }
  }, [])

  const previousArrowComponent = () => <><DatepickerPreviousIcon color={palette.mainBlue} /> {t('prev')}</>
  const nextArrowComponent = () => <>{t('next')} <DatepickerNextIcon color={palette.mainBlue} /></>

  const selectDateHandler = (date) => {
    setIsButtonDisabled(false)
    setValue(date)
    const displayDateFormatter = datePickerSettings.dateFormat === 'DOB'
      ? formatDisplayBirthDate
      : formatDisplayAppointmentDate
    setButtonTitle(displayDateFormatter(language, date))
  }

  const handleClose = () => {
    setDatePickerSettings({
      ...datePickerSettings,
      isShowSelectDate: false
    })

    removeStorageKeyFromBrowser('activeSelectDateName')
  }

  const setDateAndCloseModal = () => {
    onChange(formattedDate, buttonTitle)
    setDatePickerSettings({
      ...datePickerSettings,
      isShowSelectDate: false,
      selectedPickerDate: true
    })

    savePointNameToStorage('LOCAL_STORAGE', { questionnaireName: datePickerSettings.selectDateUUID }, 'passedSelectDatesName')
    removeStorageKeyFromBrowser('activeSelectDateName')
  }

  return (
    <Modal
      onClose={handleClose}
    >
      <ModalWrapper
        aria-label={t('date_picker')}
        role='dialog'
        aria-modal='true'
        ref={datePickerModalRef}
        onKeyDown={(e) => focusTrapCreator(e, datePickerModalRef.current, true)}
      >
        <ModalContainer>
          <ModalHeader data-test='datepicker-header'>
            <CloseButtonContainer
              ref={closeDatePickerButtonRef}
              aria-label={t('webview_close')}
              tabIndex={0}
              onClick={handleClose}
            >
              <CloseWidgetIcon color={palette.secondaryText} />
            </CloseButtonContainer>
          </ModalHeader>

          <ModalTitle data-test='datepicker-title'>
            {t('select_date')}
          </ModalTitle>

          <ModalContent
            isDobContent={true}
            data-test='datepicker-content'
          >
            <Calendar
              locale={language}
              calendarType={'US'}
              onChange={selectDateHandler}
              defaultActiveStartDate={value}
              prevLabel={previousArrowComponent()}
              prev2Label={null}
              nextLabel={nextArrowComponent()}
              next2Label={null}
              minDetail={'decade'}
              defaultView={datePickerSettings.calendarView}
              maxDate={datePickerSettings.maxDate || defaultMaxDate}
              minDate={datePickerSettings.minDate || defaultMinDate}
              formatMonth={formatMonth}
              formatMonthYear={formatMonthYear}
              formatShortWeekday={formatShortWeekday}
            />
          </ModalContent>
          <ModalFooter
            isDobContent={true}
            data-test='datepicker-footer'
          >
            <MessageButton
              data-test='footer-datepicker-button'
              isPrimaryButton={true}
              isModalButton={true}
              isButtonDisabled={isButtonDisabled}
              title={buttonTitle}
              onClickHandler={setDateAndCloseModal}
              isLoadingIframeData={isLoadingIframeData}
            />
          </ModalFooter>
        </ModalContainer>
      </ModalWrapper>
    </Modal>
  )
}

export default SelectDateModal
